/**
 * DutchMetabolitesCard — iter1113.c
 *
 * Collapsible educational card for the 17 DUTCH urine metabolites that
 * ship with `hormonal-twin/snapshot.dutch_summary`. The clinical nuance
 * of DUTCH testing (ratios > single values) means we DO NOT fold these
 * into a composite — we surface them as 4 educational panels that the
 * patient can review with their functional-med clinician.
 */
import React, { useState } from "react";
import { ChevronDown, ChevronRight, BookOpen, FlaskConical } from "lucide-react";

const PANEL_EMOJI = {
  androgen_metabolism:         "⚡",
  estrogen_detail:              "🌸",
  neurotransmitter_metabolites: "🧠",
  progesterone_metabolites:     "🌙",
};

const PANEL_ACCENT = {
  androgen_metabolism:          "border-amber-500/40 bg-amber-50 dark:bg-amber-500/5",
  estrogen_detail:              "border-pink-500/40 bg-pink-50 dark:bg-pink-500/5",
  neurotransmitter_metabolites: "border-violet-500/40 bg-violet-50 dark:bg-violet-500/5",
  progesterone_metabolites:     "border-indigo-500/40 bg-indigo-50 dark:bg-indigo-500/5",
};

export default function DutchMetabolitesCard({ summary, isDark = false }) {
  const [open, setOpen] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState(null);

  if (!summary || !summary.panels?.length) return null;

  const cardCls = isDark
    ? "bg-slate-900/60 border-slate-800"
    : "bg-white border-slate-200";

  const togglePanel = (key) =>
    setExpandedPanel((cur) => (cur === key ? null : key));

  return (
    <div
      className={`p-5 rounded-2xl border-2 mb-4 ${cardCls}`}
      data-testid="dutch-metabolites-card"
    >
      {/* Header */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 text-left"
        data-testid="dutch-card-toggle"
      >
        <FlaskConical className="w-4 h-4 text-fuchsia-500 shrink-0" />
        <div className="flex-1">
          <h2 className="font-bold text-sm leading-snug">DUTCH метаболити</h2>
          <p className="text-[11px] opacity-60 leading-snug">
            {summary.total_markers} стойности в {summary.panel_count}{" "}
            образователни панела — само за консултация
          </p>
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 opacity-60" />
        ) : (
          <ChevronRight className="w-4 h-4 opacity-60" />
        )}
      </button>

      {/* Disclaimer + Panels */}
      {open && (
        <>
          <div
            className={`mt-3 p-3 rounded-lg flex items-start gap-2 text-[11px] leading-snug ${
              isDark
                ? "bg-fuchsia-500/10 border border-fuchsia-500/30 text-fuchsia-100"
                : "bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-900"
            }`}
            data-testid="dutch-disclaimer"
          >
            <BookOpen className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{summary.disclaimer_bg}</span>
          </div>

          <div className="mt-3 space-y-2">
            {summary.panels.map((panel) => (
              <div
                key={panel.key}
                className={`rounded-lg border ${PANEL_ACCENT[panel.key] || "border-slate-500/30"} ${
                  isDark ? "border-opacity-50" : ""
                }`}
                data-testid={`dutch-panel-${panel.key}`}
              >
                <button
                  onClick={() => togglePanel(panel.key)}
                  className="w-full flex items-center gap-2 p-3 text-left"
                  data-testid={`dutch-panel-toggle-${panel.key}`}
                >
                  <span className="text-base shrink-0" aria-hidden>
                    {PANEL_EMOJI[panel.key] || "🔬"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-xs leading-tight">
                      {panel.title_bg}
                    </div>
                    <div className="text-[10px] opacity-70 leading-tight mt-0.5">
                      {panel.subtitle_bg}
                    </div>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-500/15 shrink-0">
                    {panel.markers.length}
                  </span>
                  {expandedPanel === panel.key ? (
                    <ChevronDown className="w-3.5 h-3.5 opacity-50" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 opacity-50" />
                  )}
                </button>

                {expandedPanel === panel.key && (
                  <div
                    className={`px-3 pb-3 pt-1 space-y-2 ${
                      isDark ? "border-t border-white/5" : "border-t border-slate-200/50"
                    }`}
                    data-testid={`dutch-panel-content-${panel.key}`}
                  >
                    {panel.markers.map((m) => (
                      <div
                        key={m.legacy_key}
                        className={`p-2 rounded-md text-[11px] ${
                          isDark ? "bg-slate-800/50" : "bg-white/70"
                        }`}
                        data-testid={`dutch-marker-${m.legacy_key}`}
                      >
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-semibold">{m.label_bg}</span>
                          <span className="font-bold tabular-nums ml-auto">
                            {m.value}
                            <span className="opacity-50 ml-1">{m.unit}</span>
                          </span>
                        </div>
                        <p className="opacity-75 leading-snug mt-0.5">
                          {m.note_bg}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
